import { NgClass } from '@angular/common'
import { Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { NavigationEnd, Router } from '@angular/router'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { ORGANIZATION_SETTINGS_SIDEBAR_ITEMS } from '@app/v2/settings/enums/organization-settings-sidebar-items.enum'
import { OrganizationSettingsTabsModel, Tab } from '@app/v2/settings/models/organization-settings.model'
import { TranslateModule } from '@ngx-translate/core'
import { filter } from 'rxjs'

@Component({
  selector: 'app-organization-settings-sidebar',
  templateUrl: './organization-settings-sidebar.component.html',
  styleUrls: ['./organization-settings-sidebar.component.scss'],
  standalone: true,
  imports: [NgClass, TranslateModule]
})
export class OrganizationSettingsSidebarComponent implements OnInit {
  public currentPage = input<ORGANIZATION_SETTINGS_SIDEBAR_ITEMS>(ORGANIZATION_SETTINGS_SIDEBAR_ITEMS.PUBLIC_INFO)
  public tabs = signal<Tab[]>([])
  public currentUrl = signal<string>('')
  public activeStates: Record<string, boolean> = {}
  public isAdmin = signal<boolean>(false)
  private _destroyRef = inject(DestroyRef)

  constructor(private _Sanitizer: DomSanitizer, private _Router: Router, private _authService: AuthService, private _workspaceRoleService: WorkspaceRoleService) {
    this.currentUrl.set(this._Router.url)
  }

  ngOnInit(): void {
    this.isAdmin.set(this._authService.role === 'Admin')
    this.tabs.set(this._getFilteredTabs())
    this._refreshTabs()
    this._updateActiveStates()

    this._workspaceRoleService
      .getUserRole()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.isAdmin.set(this._authService.role === 'Admin' || this._workspaceRoleService.isSuperAdmin())
        this._refreshTabs()
      })

    this._Router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects)
        this._updateActiveStates()
      })
  }

  /*
   * Refresh sidebar tabs when workspace role changes.
   */
  private _refreshTabs(): void {
    this.tabs.set(this._getFilteredTabs())
    this._ensureAccountantBillingFragment()
    this._updateActiveStates()
  }

  /*
   * Get filtered tabs based on user permissions
   * @returns - Array of tabs filtered by admin status
   */
  private _getFilteredTabs(): Tab[] {
    const allTabs = OrganizationSettingsTabsModel.getTabsConfiguration()

    // Accountants only get the billing tab; superadmins always keep the full set.
    if (this._workspaceRoleService.isAccountant() && !this._workspaceRoleService.isSuperAdmin()) {
      return allTabs.filter((tab) => tab.name === ORGANIZATION_SETTINGS_SIDEBAR_ITEMS.BILLING)
    }

    return allTabs.filter((tab) => {
      if (tab.name === ORGANIZATION_SETTINGS_SIDEBAR_ITEMS.MICETRIBE_ADMIN && !this.isAdmin()) return false
      if (tab.name === ORGANIZATION_SETTINGS_SIDEBAR_ITEMS.WEB_HOOKS && !this._workspaceRoleService.canAccessWebhooks()) return false
      if (tab.name === ORGANIZATION_SETTINGS_SIDEBAR_ITEMS.API_KEYS && !this._workspaceRoleService.canAccessApiKeys()) return false
      if (tab.name === ORGANIZATION_SETTINGS_SIDEBAR_ITEMS.MICETRIBE_ADMIN) {
        return this.isAdmin() || this._workspaceRoleService.isSuperAdmin()
      }

      if (tab.name === ORGANIZATION_SETTINGS_SIDEBAR_ITEMS.BILLING) {
        return this._workspaceRoleService.canAccessBilling()
      }

      if (tab.name === ORGANIZATION_SETTINGS_SIDEBAR_ITEMS.WEB_HOOKS && !this._workspaceRoleService.canAccessWebhooks()) return false
      if (tab.name === ORGANIZATION_SETTINGS_SIDEBAR_ITEMS.MANAGE_TEAM && !this._workspaceRoleService.canAccessManageTeam()) return false
      return true
    })
  }

  /**
   * Keep accountants on the billing fragment when opening organization settings.
   */
  private _ensureAccountantBillingFragment(): void {
    if (!this._workspaceRoleService.isAccountant() || this._workspaceRoleService.isSuperAdmin()) return

    const currentFragment = this._Router.parseUrl(this._Router.url).fragment
    if (currentFragment === 'billing') return

    this._Router.navigate([], { fragment: 'billing', replaceUrl: true, queryParamsHandling: 'preserve' })
  }

  /*
   * Update active states for all tabs based on current fragment
   */
  private _updateActiveStates(): void {
    const currentFragment = this._Router.parseUrl(this._Router.url).fragment
    this.tabs().forEach((tab) => {
      this.activeStates[tab.name] = tab.fragment === currentFragment
    })

    if (!currentFragment) return

    setTimeout(() => {
      const element = document.getElementById(currentFragment)
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  /*
   * Get CSS classes for tab styling based on active state
   * @param tab - The tab to get classes for
   * @returns - CSS classes string
   */
  private _getTabClasses(tab: Tab): string {
    return this.activeStates[tab.name] ? 'tw-text-white' : 'tw-text-gray-500 dark:tw-text-gray-400'
  }

  /*
   * Get icon with proper styling classes
   * @param tab - The tab to get icon for
   * @returns - The icon with appropriate classes
   */
  public getIcon(tab: Tab): SafeHtml {
    const iconClass = this._getTabClasses(tab)
    const iconWithClass = tab.icon.replace('<svg', `<svg class="${iconClass}"`)
    return this._Sanitizer.bypassSecurityTrustHtml(iconWithClass)
  }

  /*
   * Handle tab click and scroll to corresponding section
   * @param tab - The tab that was clicked
   */
  public onTabClick(tab: Tab): void {
    this._Router.navigate([], { fragment: tab.fragment, queryParamsHandling: 'preserve' })
  }
}
